import classNames from "classnames";
import dayjs from "dayjs";
import 'dayjs/locale/pt-br';
import React from "react";
import { withRouter } from "react-router";
import { Button, CheckPicker, DateRangePicker, Divider, FormGroup, Icon, InputNumber, Notification, Panel, Radio, RadioGroup, SelectPicker } from "rsuite";
import { ParametersGlobals } from "../../core/parametersGlobals/ParametersGlobals";
import axios from "../../utilities/axios";
import InfractionCard from "./components/Card/InfractionCard";
import { DetailsStorePieChart } from "./components/DetailsStorePieChart/detailsStorePieChart";
import EmployeeErrorPerMonthChart from "./components/EmployeeErrorPerMonthChart/employeeErrorPerMonthChart";
import EmployeeErrorPerWeekChart from "./components/EmployeeErrorPerWeekChart/employeeErrorPerWeekChart";
import FinancialChart from "./components/FinancialChart/financialChart";
import LineChartPerMonth from "./components/LineChartPerMonth/lineChartPerMonth";
import LineChartPerWeek from "./components/LineChartPerWeek/lineChartPerWeek";
import { PieChartPerPeriodWeek } from "./components/PieChartPerPeriodWeek/pieChartPerPeriodWeek";
import styles from "./styles.module.scss";
import RHChart from "./components/RHChart/rhChart";
import FinancialChartPerStore from "./components/FinancialChartPerStore/FinancialChartPerStore";
import FinancialChartPerEmployee from "./components/FinancialChartPerEmployee/FinancialChartPerEmployee";
import RHChartPerEmployee from "./components/RHChartPerEmployee/RHChartPerEmployee";
import { locale } from "../../utilities/locale";
import { CheckPickerSelectFilter } from "../../components/formfields/CheckPickerSelectFilter";

dayjs.locale('pt-br');
interface GraphsAnalyticsPageState {
    filters: any;
    financialChartData: any;
    rhChartData: any;
    data: any;
    showDetailsPieChart: boolean;
    showDetailsLineChart: boolean;
    loadingButton: boolean;
    hiddenGeneralFilters: boolean;
    hiddenMensalChartFilters: boolean;
}

class GraphsAnalyticsPage extends React.Component<any, GraphsAnalyticsPageState> {
    private pieChartRef: React.RefObject<any>;
    private detailsStorePieChartRef: React.RefObject<any>;

    constructor(props: any) {
        super(props);
        this.pieChartRef = React.createRef();
        this.detailsStorePieChartRef = React.createRef()
        this.state = {
            filters: {
                violationDateRange: [
                    dayjs().startOf('month').toDate(),
                    dayjs().endOf('month').toDate(),
                ],
                responsibleEmployee: null,
                author: null,
                platform: null,
                store: null,
                errorType: null,
                associatedOrder: 0,
                type: "period",
                selectedStore: null
            },
            financialChartData: {},
            rhChartData: {},
            data: {
                employeeData: [],
                authorsData: [],
                platformData: [],
                storeData: [],
                errorTypeData: [],
                orderData: [],
                selectFullData: [],
                errorsData: [],
                filteredErrorsData: [],
                groupErrorsData: [],
            },
            loadingButton: false,
            showDetailsPieChart: false,
            showDetailsLineChart: false,
            hiddenGeneralFilters: false,
            hiddenMensalChartFilters: false
        };
    }

    async componentDidMount(): Promise<void> {
        await this.handleGetInputInformation();
        await this.handleGetGraphsInformation();
        await this.getErrorInformations();
        await this.formatErrors();
    }

    handleSetLoadingTrue = () => {
        this.setState({ ...this.state, loadingButton: true });
    }

    handleSetLoadingFalse = () => {
        this.setState({ ...this.state, loadingButton: false });
    }

    formatErrors = async () => {
        const errors = this.state.data?.errorsData;

        if (errors) {
            const filtered = errors
                .map((e: any) => ({
                    value: Number(e.error_id),
                    label: String(e.error_name),
                    hasValue: e.error_use_value_input || false
                }));

            this.setState({
                data: {
                    ...this.state.data,
                    filteredErrorsData: filtered
                }
            })
        }
    }

    getErrorInformations = async () => {
        try {
            const response = await axios.get("/api/v1/dashboard/get_error_informations")
            const { errors, groupErrors } = response.data;

            this.setState({
                data: {
                    ...this.state.data,
                    errorsData: errors.errors,
                    groupErrorsData: groupErrors
                }
            })
        } catch (error) {
            console.log(error)
        }
    }

    handleGetInputInformation = async () => {
        try {
            const response = await axios.get("/api/v1/dashboard/get_informations");
            if (response?.data) {
                const formattedUsers = Array.isArray(response.data.users)
                    ? response.data.users.map((user: any) => ({
                        value: user?.user_id || '',
                        label: user?.user_name || 'Unknown User',
                    }))
                    : [];

                const formattedAuthors = Array.isArray(response.data.authors)
                    ? response.data.authors.map((author: any) => ({
                        value: author?.user_id || '',
                        label: author?.user_name || 'Unknown Author',
                    }))
                    : [];

                this.setState({
                    data: { ...this.state.data, authorsData: formattedAuthors },
                    filters: {
                        ...this.state.filters,
                        author: formattedAuthors.map((a: any) => a.value)
                    }
                });

                const formattedPlatforms = Array.isArray(response.data.platforms)
                    ? response.data.platforms.map((platform: any) => ({
                        value: platform?.platform_id || '',
                        label: platform?.platform_name || 'Unknown Platform',
                    }))
                    : [];

                const formattedStores = Array.isArray(response.data.stores)
                    ? response.data.stores.map((store: any) => ({
                        value: store?.store_id || '',
                        label: store?.store_name || 'Unknown Store',
                    }))
                    : [];

                const formattedErrors = Array.isArray(response.data.errors)
                    ? response.data.errors.map((error: any) => ({
                        value: error?.error_id || '',
                        label: error?.error_name || 'Unknown Error',
                    }))
                    : [];

                this.setState({
                    data: {
                        employeeData: formattedUsers,
                        authorsData: formattedAuthors,
                        platformData: formattedPlatforms,
                        storeData: formattedStores,
                        errorTypeData: formattedErrors,
                    },
                });

                if (formattedStores && formattedStores.length > 0) {
                    this.setState({
                        filters: {
                            ...this.state.filters,
                            selectedStore: formattedStores?.[0]?.value || null
                        }
                    })
                }
            } else {
                this.setState({
                    data: {
                        employeeData: [],
                        authorsData: [],
                        platformData: [],
                        storeData: [],
                        errorTypeData: [],
                    },
                });
            }
        } catch (error) {
            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar as informações dos filtros",
                duration: 3500
            })

            console.log("Erro ao buscar as informações para os selects ::::>> ", error)
        }
    }

    handleGetGraphsInformation = async () => {
        this.handleSetLoadingTrue()

        const author = this.state.filters.author
        if (!author || author.length === 0) {
            this.setState({
                filters: {
                    ...this.state.filters,
                    author: this.state.data.authorsData.map((a: any) => a.value)
                }
            })
        }
        try {
            if (this.pieChartRef?.current?.handleGetChartData) {
                await this.pieChartRef.current.handleGetChartData();
            } else {
                console.warn("pieChartRef não está pronto");
            }

            if (this.detailsStorePieChartRef?.current?.handleGetChartData) {
                await this.detailsStorePieChartRef.current.handleGetChartData();
            } else {
                console.warn("detailsStorePieChartRef não está pronto");
            }
        } catch (error) {
            console.error("Erro ao atualizar o gráfico:", error);
        }
        this.handleSetLoadingFalse();
    };

    handleFilterChange = (key: string, value: any) => {
        this.setState(
            (prevState) => ({
                filters: {
                    ...prevState.filters,
                    [key]: value,
                },
            }),
            async () => {
                if (key === "store") {
                    const { storeData } = this.state.data;
                    const selectedItems = storeData.filter((item: any) => value.includes(item.value))
                    const selectedLabels = selectedItems.map((item: any) => item.label)
                    await this.detailsStorePieChartRef.current.applyPieChartFilter(selectedLabels);
                }
            }
        );
    };

    clearFilters = async () => {
        this.handleSetLoadingTrue();

        await new Promise((resolve) => {
            this.setState(
                {
                    filters: {
                        violationDateRange: [
                            dayjs().startOf('month').toDate(),
                            dayjs().endOf('month').toDate(),
                        ],
                        responsibleEmployee: null,
                        author: this.state.data.authorsData.map((a: any) => a.value),
                        platform: null,
                        store: null,
                        errorType: null,
                        associatedOrder: 0,
                        type: "period",
                        selectedStore: this.state.data.storeData[0]?.value
                    },
                },
                //@ts-ignore
                resolve
            );
        });

        await this.handleGetGraphsInformation();
        this.handleSetLoadingFalse();
    };

    handleShowGeneralFilters = () => {
        this.setState({
            hiddenGeneralFilters: !this.state.hiddenGeneralFilters
        })
    }

    handleShowMensalChartFilters = () => {
        this.setState({
            hiddenMensalChartFilters: !this.state.hiddenMensalChartFilters
        })
    }

    render() {
        return (
            <>
                <div style={{ marginLeft: localStorage.getItem("hiddenSidebar") == "true" ? "0px" : "203px" }} className={classNames("container")}>
                    <div className={styles.mainContainer}>
                        <div className={styles.containerCards}>
                            <InfractionCard />
                        </div>

                        <div className={styles.dropdownDivider}>
                            {this.state.hiddenGeneralFilters ? (
                                <>
                                    <div style={{ width: '100%', marginTop: '3rem' }}>

                                    </div>
                                    <Icon icon='up' className={styles.dropdownIcon} onClick={this.handleShowGeneralFilters} />
                                </>
                            ) : (
                                <>
                                    <Divider style={{ width: '100%' }}>Filtros Gerais</Divider>
                                    <Icon icon='down' className={styles.dropdownIcon} onClick={this.handleShowGeneralFilters} />
                                </>
                            )}


                        </div>

                        {!this.state.hiddenGeneralFilters && (
                            <div className={styles.filterLine}>
                                <div className={styles.filterDiv} style={{ width: '245px' }}>
                                    <span>Período</span>
                                    <DateRangePicker
                                        placeholder="Selecione uma data"
                                        format="DD/MM/YYYY"
                                        ranges={[]}
                                        onChange={(value) => this.handleFilterChange('violationDateRange', value)}
                                        value={this.state.filters.violationDateRange}
                                        locale={locale}
                                    />
                                </div>

                                <div className={styles.filterDiv} style={{ width: '100px' }}>
                                    <span>Pedido</span>
                                    <InputNumber
                                        value={this.state.filters.associatedOrder}
                                        onChange={(value) => this.handleFilterChange('associatedOrder', value)}
                                    />
                                </div>

                                <div className={styles.filterDiv} style={{ flex: 1, minWidth: 0 }}>
                                    <span>Autor</span>
                                    <CheckPicker
                                        className="force-rs-inline"
                                        data={this.state.data.authorsData}
                                        placeholder="Selecione um autor"
                                        onChange={(value) => this.handleFilterChange('author', value)}
                                        value={this.state.filters.author}
                                    />
                                </div>

                                <div className={styles.filterDiv} style={{ flex: 1, minWidth: 0 }}>
                                    <span>Funcionário</span>

                                    <CheckPicker
                                        className="force-rs-inline"
                                        data={this.state.data.employeeData}
                                        placeholder="Selecione um funcionário"
                                        onChange={(value) =>
                                            this.handleFilterChange('responsibleEmployee', value)
                                        }
                                        value={this.state.filters.responsibleEmployee}
                                    />
                                </div>

                                <div className={styles.filterDiv} style={{ flex: 1, minWidth: 0 }}>
                                    <span>Lançamento</span>

                                    <CheckPickerSelectFilter
                                        className="force-rs-inline"
                                        onSelectFilter={(value) => this.handleFilterChange('errorType', value)}
                                        data={this.state.data.errorTypeData}
                                        placeholder="Selecione Lançamentos..."
                                        value={this.state.filters.errorType}
                                        onChange={(value) => this.handleFilterChange('errorType', value)}
                                    />
                                </div>

                                <div className={styles.filterDiv} style={{ width: '150px' }}>
                                    <div className={styles.filterButtons}>
                                        <Button
                                            size="sm"
                                            appearance="primary"
                                            color="blue"
                                            style={{ flex: 1 }}
                                            onClick={this.handleGetGraphsInformation}
                                            loading={this.state.loadingButton}
                                        >
                                            Filtrar
                                        </Button>

                                        <Button
                                            size="sm"
                                            appearance="primary"
                                            color="orange"
                                            style={{ flex: 1 }}
                                            onClick={this.clearFilters}
                                            loading={this.state.loadingButton}
                                        >
                                            Limpar Filtros
                                        </Button>

                                    </div>
                                </div>
                            </div>
                        )}

                        {!this.state.hiddenGeneralFilters && (
                            <Divider>Gráficos</Divider>
                        )}

                        <div className={styles.chartsContainer}>
                            <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
                                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div className={styles.filters}>
                                        <div className={styles.filterDiv}>
                                            <span>Loja</span>
                                            <SelectPicker
                                                data={this.state.data.storeData}
                                                value={this.state.filters.selectedStore}
                                                placeholder="Selecione uma loja..."
                                                locale={{
                                                    noResultsText: "Nenhuma loja encontrada"
                                                }}
                                                onChange={(value) =>
                                                    this.setState((prevState) => ({
                                                        filters: {
                                                            ...prevState.filters,
                                                            selectedStore: value,
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>
                                        <div style={{ width: '100%' }}>
                                            <FormGroup controlId="radioList">
                                                <span>‎ </span>
                                                <RadioGroup
                                                    name="radioList"
                                                    inline
                                                    style={{ display: 'flex' }}
                                                    value={this.state.filters.type}
                                                    onChange={(value) =>
                                                        this.setState((prevState) => ({
                                                            filters: {
                                                                ...prevState.filters,
                                                                type: value,
                                                            },
                                                        }))
                                                    }
                                                >
                                                    <Radio value="period">Período</Radio>
                                                    <Radio value="weekDays">Dias da Semana</Radio>
                                                </RadioGroup>
                                            </FormGroup>
                                        </div>
                                    </div>

                                    <PieChartPerPeriodWeek
                                        ref={this.pieChartRef}
                                        associatedOrder={this.state.filters.associatedOrder}
                                        author={this.state.filters.author}
                                        selectedStore={this.state.filters.selectedStore}
                                        violationDateRange={this.state.filters.violationDateRange}
                                        type={this.state.filters.type}
                                        responsibleEmployee={this.state.filters.responsibleEmployee}
                                        errorType={this.state.filters.errorType}
                                    />

                                </div>

                                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div className={styles.filterDiv}>
                                        <span>Loja</span>
                                        <CheckPicker
                                            data={this.state.data.storeData}
                                            placeholder="Selecione uma Loja"
                                            onChange={(value) => this.handleFilterChange('store', value)}
                                            value={this.state.filters.store}
                                        />
                                    </div>

                                    <DetailsStorePieChart
                                        ref={this.detailsStorePieChartRef}
                                        associatedOrder={this.state.filters.associatedOrder}
                                        author={this.state.filters.author}
                                        platform={this.state.filters.platform}
                                        responsibleEmployee={this.state.filters.responsibleEmployee}
                                        store={this.state.filters.store}
                                        violationDateRange={this.state.filters.violationDateRange}
                                        errorType={this.state.filters.errorType}
                                    />

                                </div>
                            </div>

                            {/* <Divider>Gráfico de erro detalhado por funcionário</Divider>

                            <ErrorPerEmployeeDateChart
                                employees={this.state.data.employeeData}
                            /> */}

                            <Divider>Gráfico Semanal</Divider>


                            <LineChartPerWeek
                                stores={this.state.data.storeData}
                                platforms={this.state.data.platformData}
                                errors={this.state.data.filteredErrorsData}
                            />

                            <EmployeeErrorPerWeekChart
                                employee={this.state.data.employeeData}
                                errors={this.state.data.filteredErrorsData}
                            />

                            <div className={styles.dropdownDivider}>
                                {this.state.hiddenMensalChartFilters ? (
                                    <>
                                        <div style={{ width: '100%', marginTop: '3rem' }}>

                                        </div>
                                        <Icon icon='up' className={styles.dropdownIcon} onClick={this.handleShowMensalChartFilters} />
                                    </>
                                ) : (
                                    <>
                                        <Divider style={{ width: '100%' }}>Gráfico Mensal</Divider>
                                        <Icon icon='down' className={styles.dropdownIcon} onClick={this.handleShowMensalChartFilters} />
                                    </>
                                )}
                            </div>

                            <LineChartPerMonth
                                showFilters={this.state.hiddenMensalChartFilters}
                                stores={this.state.data.storeData}
                                platforms={this.state.data.platformData}
                                errors={this.state.data.filteredErrorsData}
                            />

                            <EmployeeErrorPerMonthChart
                                employee={this.state.data.employeeData}
                                errors={this.state.data.filteredErrorsData}
                            />

                            <Panel
                                bordered
                                style={{
                                    backgroundColor: '#f9f9f9',
                                    borderRadius: '10px',
                                }}
                            >
                                <FinancialChart
                                    employees={this.state.data.employeeData}
                                    stores={this.state.data.storeData}
                                />

                                <Divider />

                                <h3>Financeiro por Loja</h3>

                                <FinancialChartPerStore
                                    storesData={this.state.data.storeData}
                                />

                                <Divider />

                                <h3> Financeiro por Funcionário</h3>

                                <FinancialChartPerEmployee
                                    employeesData={this.state.data.employeeData}
                                />

                                <Divider />

                                <RHChart />

                                <Divider />

                                <h3> RH por Funcionário e Loja</h3>

                                <RHChartPerEmployee
                                    employeesData={this.state.data.employeeData}
                                    storesData={this.state.data.storeData}
                                />
                            </Panel>
                        </div>
                    </div>
                </div >
            </>
        )
    }
}

export default withRouter(GraphsAnalyticsPage);
